import React from 'react';
import { Button } from '../Button';
import { useMediaQuery } from '../HomePageBanner/hooks/useMediaQuery';
import { Mode } from '../Mode';
import { PlusCircleSvg } from '../svgs/PlusOutlinedIcon';
import styles from './WhyAthena.module.scss';

type WhyAthenaModalContentProps = Array<{
  title: string | React.ReactNode;
  body?: React.ReactNode;
}>;

export type PrimaryProofPointProps = {
  title: string;
  buttonText: string;
  /*
    NOTE: Only provide this if you want an anchor to link to an external page
    and avoid the current modal functionality.
  */
  buttonLink?: string;
  paragraph: React.ReactNode;
  imageSrcDesktop: string;
  imageSrcMobile: string;
  imageAlt: string;
  modalContent: WhyAthenaModalContentProps | null;
};

export type SecondaryProofPointProps = {
  image: React.ReactNode;
  title: string;
  body: string;
};

const ModalContent = ({
  content,
}: {
  content: Array<{
    title: string | React.ReactNode;
    body?: string | React.ReactNode;
  }>;
}) => (
  <div className={styles.modalContent}>
    {content.map(({ title, body }) => (
      <div className={styles.modalParagraph} key={`${title}`}>
        <div className={styles.modalTitle}>{title}</div>
        {body && <div className={styles.modalBody}>{body}</div>}
      </div>
    ))}
  </div>
);

const PrimaryProofPointCard = ({
  title,
  paragraph,
  buttonText,
  buttonLink = '',
  imageAlt,
  imageSrcDesktop,
  imageSrcMobile,
  modalContent,
  setModalContent,
  openModal,
}: PrimaryProofPointProps & { setModalContent: (content: React.ReactNode) => void; openModal: () => void }) => {
  const { isMobile } = useMediaQuery();

  return (
    <div className={styles.cardContainer}>
      {isMobile ? (
        <img
          className={styles.cardImage}
          src={imageSrcMobile}
          alt={imageAlt}
          width="375px"
          height="225px"
          loading="lazy"
        />
      ) : (
        <img
          className={styles.cardImage}
          src={imageSrcDesktop}
          alt={imageAlt}
          width="354px"
          height="224px"
          loading="lazy"
        />
      )}

      <div className={styles.card}>
        <div className={styles.cardText}>
          <h3 className={styles.cardTitleLarge}>{title}</h3>
          <div className={styles.cardParagraph}>{paragraph}</div>
        </div>
        {/* Only show modal if buttonLink isn't specified */}
        {buttonText && !buttonLink && (
          <button
            type="button"
            className={styles.cardButton}
            onClick={() => {
              if (modalContent) {
                setModalContent(<ModalContent content={modalContent} />);
                openModal();
              }
            }}
          >
            <span className={styles.cardButtonText}>{buttonText}</span>
            <PlusCircleSvg />
          </button>
        )}
        {buttonLink && !modalContent && (
          <Button args={{ className: styles.cardButton }} mode={Mode.LIGHT} icon="r" type="outline" href={buttonLink}>
            <span className={styles.cardButtonText}>{buttonText}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

const SecondaryProofPointCard = ({ image, title, body }: SecondaryProofPointProps) => (
  <div className={styles.secondaryPoint}>
    <div>{image}</div>
    <div className={styles.secondaryCard}>
      <h3 className={styles.cardTitleMedium}>{title}</h3>
      <div className={styles.cardParagraph}>{body}</div>
    </div>
  </div>
);

export const WhyAthena = ({
  primaryProofPoints,
  secondaryProofPoints,
  setModalContent,
  openModal,
}: {
  primaryProofPoints: Array<PrimaryProofPointProps>;
  secondaryProofPoints: Array<SecondaryProofPointProps>;
  setModalContent: (content: React.ReactNode) => void;
  openModal: () => void;
}) => (
  <div className={styles.fullWidth}>
    <h2 className={styles.title}>Why choose Athena?</h2>
    <div className={styles.content}>
      <div className={styles.primaryContainer}>
        {primaryProofPoints.map((props, index) => (
          <PrimaryProofPointCard {...props} key={index} setModalContent={setModalContent} openModal={openModal} />
        ))}
      </div>
      <div className={styles.secondaryContainer}>
        {secondaryProofPoints.map((props, index) => (
          <SecondaryProofPointCard {...props} key={index} />
        ))}
      </div>
    </div>
    <Button mode={Mode.LIGHT} icon="r" type="outline" href="/about">
      Read more about Athena
    </Button>
    <div className={styles.transitionShape} />
  </div>
);
